import {Injectable} from '@angular/core';
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {Observable} from "rxjs";
import {TermsOfAgreement} from "../models/terms-of-agreement.model";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";

/**
 * Serwis warstwy danych zarządzający dokumentacją prawną, regulaminami
 * oraz warunkami umów (Terms of Agreement).
 * Obsługuje wielopoziomową architekturę zgód (poziom Klienta oraz poziom Kliniki)
 * wraz z procesem publikacji i wersjonowania szablonów.
 */
@Injectable({
  providedIn: 'root'
})
export class TermsOfAgreementService {

  constructor(private apiService: ApiService, public commonUtils : CommonUtils) {
  }

  /**
   * Pobiera aktualnie obowiązujące warunki umowy dla konkretnej placówki (Kliniki).
   *
   * @param vetcodeId Unikalny numer identyfikacyjny kliniki.
   * @returns Observable emitujący obiekt warunków umowy.
   */
  getAgreement(vetcodeId: number): Observable<TermsOfAgreement> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/terms-of-agreement/${vetcodeId}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new TermsOfAgreement(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera wygenerowany obiekt `TermsOfAgreement` dla danej placówki.
   *
   * @param vetcodeId Unikalny numer identyfikacyjny kliniki.
   * @returns Observable emitujący obiekt wygenerowanej umowy.
   */
  getGeneratedAgreement(vetcodeId: number): Observable<TermsOfAgreement> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/generated-terms-of-agreement/${vetcodeId}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new TermsOfAgreement(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera globalny szablon regulaminu / umowy (Template)
   * bazujący na aktualnych uwarunkowaniach prawnych.
   *
   * @returns Observable emitujący treść szablonu (najczęściej w formacie HTML lub znaczników tekstowych).
   */
  getTemplate(): Observable<string> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/dt/toa`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response.value;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje akceptację zaktualizowanych warunków umowy.
   *
   * @param termsOfAgreement Obiekt zawierający warunku umowy.
   * @returns Observable emitujący potwierdzony obiekt umowy z serwera.
   */
  addAgreement(termsOfAgreement: TermsOfAgreement): Observable<TermsOfAgreement> {
    return this.apiService.put<VetineoApiResponse>(`/dashboard/terms-of-agreement`,termsOfAgreement).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new TermsOfAgreement(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera wszystkie dokumenty prawne przypisane do nadrzędnego klienta.
   *
   * @param vClientUUID Identyfikator nadrzędnego klienta.
   * @returns Observable emitujący tablicę wszystkich powiązanych umów.
   */
  getAgreementsForClient(vClientUUID: string): Observable<TermsOfAgreement[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/client-terms-of-agreement/${vClientUUID}`).
    pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new TermsOfAgreement(),[], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera wysoce zindywidualizowaną umowę (tzw. Custom Agreement)
   * zaimportowaną specjalnie dla danego klienta.
   *
   * @param vClientUUID Identyfikator klienta.
   * @returns Observable emitujący indywidualny profil umowy.
   */
  getIndividualAgreementForClient(vClientUUID: string): Observable<TermsOfAgreement> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/toa/imported/${vClientUUID}`).
    pipe(take(1),map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new TermsOfAgreement(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Publikuje przygotowaną wcześniej, zindywidualizowaną umowę.
   *
   * @param toaUUID Unikalny identyfikator dokumentu prawnego.
   * @returns Observable emitujący opublikowany obiekt umowy.
   */
  publishIndividualAgreement(toaUUID: string): Observable<TermsOfAgreement> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/toa/publish/${toaUUID}`).
    pipe(take(1),map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new TermsOfAgreement(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Weryfikuje, czy występuje nowa wersja regulaminu/szablonu,
   * która wymaga ponownej akceptacji przez administratorów danego klienta.
   *
   * @param vClientUUID Identyfikator klienta B2B.
   * @returns Observable zwracający status dostępności nowego regulaminu lub rzucający błąd w przypadku nieprawidłowych danych.
   */
  checkIfNewTemplateExists(vClientUUID: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/toa-check/${vClientUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.response) {
        return vetineoApiResponse.response;
      } else {
        if(vetineoApiResponse.errors && vetineoApiResponse.errors.main === 'WRONG_CLIENT_DATA'){
          throw new Error(vetineoApiResponse.errors.main);
        } else {
          return undefined;
        }
      }
    }));
  }
}
